<template>
    <div>
        <common-step-progressbar
            :isClickable="true"
            :steps="progressbarSteps"
            :currentStep="progressbarCurrentStep"
            @go-to-step="goToStep"
        ></common-step-progressbar>

        <common-page-plain>
            <div class="configure-offer-page__buttons">
                <common-buttons align="left" class="buttons__spacing--small">
                    <div>
                        <common-button
                            v-if="showBackButton"
                            :isSubmittable="false"
                            type="secondary"
                            size="medium-small"
                            :label="$t('common.microcopy.back')"
                            @click="goToPreviousStep"
                        ></common-button>
                    </div>
                </common-buttons>
                <common-buttons align="right" class="buttons__spacing--small">
                    <common-button
                        v-if="progressbarCurrentStep === 1 || progressbarCurrentStep === 2"
                        :isSubmittable="false"
                        type="primary"
                        size="medium-small"
                        :label="$t('common.microcopy.next')"
                        @click="goToNextStep"
                    ></common-button>

                    <div v-if="(progressbarCurrentStep > 2 && !showExistingAvailabilityComponent) || showGoToOffers">
                        <common-content-spacer height="small">
                            <div :align="buttonAlignment">
                                <common-button
                                    v-if="progressbarCurrentStep > 2 && !showExistingAvailabilityComponent"
                                    :isSubmittable="false"
                                    type="quaternary"
                                    size="medium-small"
                                    :label="createNewButtonLabel"
                                    @click="toggleAvailability()"
                                ></common-button>
                            </div>
                            <div :align="buttonAlignment">
                                <common-button
                                    v-if="progressbarCurrentStep > 2 && !showAddAvailabilityComponent"
                                    :isSubmittable="false"
                                    type="primary"
                                    size="medium-small"
                                    :label="useExistingButtonLabel"
                                    @click="toggleExistingAvailability()"
                                ></common-button>
                            </div>
                            <div :align="buttonAlignment">
                                <common-button
                                    v-if="showGoToOffers"
                                    :isSubmittable="false"
                                    type="secondary"
                                    size="medium-small"
                                    :label="$t('pages.offer.configure_offer.buttons.go_to_offers')"
                                    @click="goToOffers()"
                                ></common-button>
                            </div>
                        </common-content-spacer>
                    </div>
                </common-buttons>
            </div>
            <common-content-spacer height="medium">
                <common-page-header :heading="pageHeading" :content="pageContent" :isCenter="true"></common-page-header>

                <add-availability
                    v-if="showAddAvailabilityComponent"
                    :isOfferForComapny="isOfferForComapny"
                    :prefill="selectedAvailability"
                    :offer="offer"
                    :existingAvailability="existingAvailability"
                    @availability-added="availabilityAdded"
                ></add-availability>
                <offer-availability
                    v-if="showExistingAvailabilityComponent"
                    :allowSelection="true"
                    :timeZone="offer.location.timeZone"
                    :availabilityOffers="existingAvailability"
                    :companyId="companyId"
                    :isOfferForComapny="isOfferForComapny"
                    @availability-added="availabilityAddedToOffer"
                    @availability-clicked="availabilityClicked"
                    @back="close"
                ></offer-availability>

                <common-content-wrapper v-else-if="progressbarCurrentStep === 0" width="medium">
                    <create-offer-form :prefill="offer" @offer-changed="offerChanged"></create-offer-form>
                </common-content-wrapper>

                <common-content-wrapper v-else-if="progressbarCurrentStep === 1" width="huge">
                    <create-offer-categories-form :prefill="offer" @offer-changed="loadData"></create-offer-categories-form>
                </common-content-wrapper>

                <multiple-image-upload
                    v-else-if="progressbarCurrentStep === 2"
                    :photos="offer.photos"
                    :uploadAPI="uploadPhotosApi"
                    :mainImageType="mainImageType"
                    @next-step="goToNextStep()"
                    @remove-photo="removePhoto($event)"
                    @set-main-photo="setMainPhoto($event)"
                    @load-photos="loadData"
                ></multiple-image-upload>
                <div v-else-if="!showAddAvailabilityComponent">
                    <offer-availability
                        :timeZone="offer.location.timeZone"
                        :availabilityOffers="offer.availabilityOffers"
                        @availability-clicked="availabilityClicked"
                        @availability-removed="loadData()"
                    ></offer-availability>
                </div>
            </common-content-spacer>
        </common-page-plain>
        <common-yes-no-modal
            v-if="isAddCategoryWarningModalOpen"
            height="tiny"
            :headingCenter="true"
            :headingText="$t('pages.offer.create_offer.category_warning')"
            :noButtonText="$t('common.microcopy.ok')"
            @close="isAddCategoryWarningModalOpen = false"
        ></common-yes-no-modal>
    </div>
</template>

<script>
    import CreateOfferForm from 'components/forms/offer/CreateOfferForm'
    import CreateOfferCategoriesForm from 'components/forms/offer/CreateOfferCategoriesForm'
    import MultipleImageUpload from 'components/page-elements/images/MultipleImageUpload'
    import OfferAvailability from 'components/page-elements/availability/OfferAvailability'
    import AddAvailability from 'components/page-elements/availability/AddAvailability'
    import OfferAPI from 'api/OfferAPI'
    import OfferTypeEnum from 'enums/offer_type.enum'
    import PhotoTypeEnum from 'enums/photo_type.enum'
    import cloneDeep from 'lodash.clonedeep'
    import UsersAPI from 'api/UsersAPI'
    import CompanyAPI from 'api/CompanyAPI'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'
    import RoutesEnum from 'enums/routes.enum'
    import TranslateMixin from 'mixins/TranslateMixin'

    export default {
        mixins: [WindowResizeMixin, TranslateMixin],

        components: { CreateOfferForm, CreateOfferCategoriesForm, MultipleImageUpload, OfferAvailability, AddAvailability },

        data() {
            return {
                progressbarCurrentStep: 1,

                offer: {},
                isOfferForComapny: false,
                showAddAvailabilityComponent: false,
                showExistingAvailabilityComponent: false,
                selectedAvailability: undefined,
                existingAvailability: [],
                companyId: null,
                isAddCategoryWarningModalOpen: false,
            }
        },

        computed: {
            pageHeading() {
                if (this.progressbarCurrentStep === 1) {
                    return this.$t('pages.offer.configure_offer.categories')
                }
                if (this.showAddAvailabilityComponent) {
                    return this.$t('pages.offer.configure_offer.create_new')
                }
                if (this.showExistingAvailabilityComponent) {
                    return this.$t('pages.offer.configure_offer.choose_existing')
                }
                if (this.isUploadStep) {
                    return this.$t('pages.offer.configure_offer.upload_photos.heading')
                }
                if (this.isScheduleStep) {
                    return this.$t('pages.offer.configure_offer.schedule.heading')
                }
                return this.$t('pages.offer.create_offer.title_modify')
            },
            progressbarSteps() {
                return [
                    this.$t('pages.offer.create_offer.progressbar.service_data'),
                    this.$t('pages.offer.create_offer.progressbar.category'),
                    this.$t('pages.offer.create_offer.progressbar.photos'),
                    this.$t('pages.offer.create_offer.progressbar.schedule'),
                ]
            },
            pageContent() {
                if (this.progressbarCurrentStep === 1) {
                    return ''
                }
                if (this.isUploadStep) {
                    return this.$t('pages.offer.configure_offer.upload_photos.content')
                }
                if (this.isScheduleStep) {
                    if (this.showAddAvailabilityComponent) {
                        return this.$t('pages.offer.configure_offer.schedule.content_create')
                    }
                    return this.$t('pages.offer.configure_offer.schedule.content')
                }
                return this.$t('pages.offer.create_offer.subtitle')
            },
            isUploadStep() {
                return this.progressbarCurrentStep === 2
            },
            isScheduleStep() {
                return this.progressbarCurrentStep === 3
            },
            showGoToOffers() {
                return (
                    this.progressbarCurrentStep === 3 &&
                    this.offer.availabilityOffers.length &&
                    !this.showAddAvailabilityComponent &&
                    !this.showExistingAvailabilityComponent
                )
            },
            actionButtonLabel() {
                if (this.showAddAvailabilityComponent) {
                    return this.$t('common.microcopy.back')
                }
                return this.$t('common.microcopy.add_plus')
            },
            mainImageType() {
                return PhotoTypeEnum.OFFER_MAIN
            },
            createNewButtonLabel() {
                return this.progressbarCurrentStep > 2 && !this.showAddAvailabilityComponent
                    ? this.$t('pages.offer.configure_offer.buttons.create_new')
                    : this.$t('common.microcopy.back')
            },
            useExistingButtonLabel() {
                return this.progressbarCurrentStep > 2 && !this.showExistingAvailabilityComponent
                    ? this.$t('pages.offer.configure_offer.buttons.use_existing')
                    : this.$t('common.microcopy.back')
            },

            buttonAlignment() {
                return this.isSmartphoneLarge ? 'center' : 'right'
            },

            showBackButton() {
                return this.progressbarCurrentStep > 0 && !this.showAddAvailabilityComponent
            },
        },

        methods: {
            goToNextStep() {
                if (this.progressbarCurrentStep === 1 && !this.offer?.offerCategories?.length) {
                    this.isAddCategoryWarningModalOpen = true
                    return
                }
                this.progressbarCurrentStep = this.progressbarCurrentStep + 1
                this.hideExistingAvailabilityComponent()
            },
            goToPreviousStep() {
                if (!this.showExistingAvailabilityComponent) {
                    this.progressbarCurrentStep = this.progressbarCurrentStep - 1
                }
                this.hideExistingAvailabilityComponent()
            },

            goToStep(step) {
                if (step > 1 && !this.offer?.offerCategories?.length) {
                    this.isAddCategoryWarningModalOpen = true
                    return
                }
                this.progressbarCurrentStep = step
                this.showAddAvailabilityComponent = false
                this.hideExistingAvailabilityComponent()
            },
            hideExistingAvailabilityComponent() {
                this.showExistingAvailabilityComponent = false
            },
            loadOfferData() {
                let req = OfferAPI.getUserOfferById
                if (this.isOfferForComapny) {
                    req = OfferAPI.getCompanyOfferById
                }
                req(this.$route.params.id)
                    .then(() => {})
                    .catch(() => {})
            },
            toggleAvailability(availability) {
                this.showAddAvailabilityComponent = !this.showAddAvailabilityComponent
                this.selectedAvailability = availability
                if (this.showAddAvailabilityComponent) {
                    setTimeout(() => {
                        window.scrollTo(0, 260)
                    })
                }
            },
            toggleExistingAvailability() {
                this.showExistingAvailabilityComponent = !this.showExistingAvailabilityComponent
            },

            availabilityAddedToOffer() {
                if (!this.$auth.getUser().hasAnyAvailability) {
                    this.$auth.reloadUser()
                }
                this.hideExistingAvailabilityComponent()
                this.loadData(true)
            },
            offerChanged(offer) {
                let isShowToastSuccesMessage = false
                if (offer) {
                    this.offer = offer
                    isShowToastSuccesMessage = true
                }
                this.loadData(isShowToastSuccesMessage)
                this.goToNextStep()
            },
            loadData(isShowToastSuccesMessage) {
                return OfferAPI.getOfferById(this.$route.params.id).then((offer) => {
                    offer.photos = [
                        ...offer.photos.filter((photo) => photo.type === PhotoTypeEnum.OFFER_MAIN),
                        ...offer.photos.filter((photo) => photo.type !== PhotoTypeEnum.OFFER_MAIN),
                    ]
                    this.offer = offer
                    this.companyId = this.offer?.location?.companyServiceProvider?.id
                    if (this.companyId) {
                        this.isOfferForComapny = true
                    }
                    this.isOfferForComapny ? this.getCompanyAvailability() : this.getUserAvailability()
                    if (isShowToastSuccesMessage) {
                        this.$toast.success(this.$t('common.microcopy.success'))
                    }
                })
            },

            hideAddAvailabilityComponent() {
                this.showAddAvailabilityComponent = false
            },
            availabilityAdded() {
                if (!this.$auth.getUser().hasAnyAvailability) {
                    this.$auth.reloadUser()
                }

                this.loadData(true).then(() => this.hideAddAvailabilityComponent())
            },
            availabilityClicked({ availability }) {
                this.toggleAvailability(cloneDeep(availability))
            },
            uploadPhotosApi(files) {
                const shouldUploadMainPhoto = !this.offer.photos?.length
                for (let i = 0; i < files.length; i++) {
                    if (i === 0 && shouldUploadMainPhoto) {
                        files[i].type = PhotoTypeEnum.OFFER_MAIN
                    } else {
                        files[i].type = PhotoTypeEnum.OFFER_OTHER
                    }
                }
                return OfferAPI.uploadPhotos({
                    id: this.offer.id,
                    files,
                })
            },
            removePhoto(file) {
                OfferAPI.removePhoto(file.id).then(() => this.loadData(true))
            },
            setMainPhoto(file) {
                OfferAPI.setMainPhoto(file.id).then(() => this.loadData(true))
            },
            getUserAvailability() {
                UsersAPI.getUserAvailability().then((response) => {
                    this.existingAvailability = response
                        .filter((item) => !item.linkedOffers.map((offer) => offer.id).includes(Number(this.$route.params.id)))
                        .map((item) => {
                            return {
                                id: item.id,
                                availability: {
                                    id: item.id,
                                    titleTextContent: item?.titleTextContent,
                                    timeline: item.timeline,
                                    availabilityUsers: item.availabilityUsers,
                                },
                                showAddButton: true,
                                linkedOffers: item.linkedOffers.map((offer) => {
                                    return {
                                        id: offer.id,
                                        name: offer?.titleTextContent?.originalText,
                                        redirectRoute: `/offer/${offer.id}/configure/user`,
                                    }
                                }),
                            }
                        })
                })
            },

            getCompanyAvailability() {
                CompanyAPI.getCompanyAvailability(this.offer?.location?.companyServiceProvider?.id).then((response) => {
                    this.existingAvailability = response
                        .filter((item) => {
                            const alreadyAssigned = !item.linkedOffers
                                .map((offer) => offer.id)
                                .includes(Number(this.$route.params.id))
                            const isUserPerformerOrModerator = item.availabilityUsers
                                .map((au) => au.user.id)
                                .includes(this.$auth.getUser().id)
                            return alreadyAssigned && isUserPerformerOrModerator
                        })
                        .map((item) => {
                            return {
                                id: item.id,
                                availability: {
                                    id: item.id,
                                    titleTextContent: item?.titleTextContent,
                                    timeline: item.timeline,
                                    availabilityUsers: item.availabilityUsers,
                                },
                                showAddButton: !item.linkedOffers
                                    .map((offer) => offer.id)
                                    .includes(Number(this.$route.params.id)),
                                linkedOffers: item.linkedOffers.map((offer) => {
                                    return {
                                        id: offer.id,
                                        name: offer?.titleTextContent?.originalText,
                                        redirectRoute: `/offer/${offer.id}/configure/user`,
                                    }
                                }),
                            }
                        })
                })
            },

            goToOffers() {
                if (this.isOfferForComapny) {
                    this.$router.push({
                        name: RoutesEnum.COMPANY.MY_COMPANY,
                        params: { id: this.companyId, tabType: 'services' },
                        query: { tab: 'services' },
                    })
                } else {
                    this.$router.push({ name: RoutesEnum.USER.ME, params: { tabType: 'services' }, query: { tab: 'services' } })
                }
            },
        },

        beforeMount() {
            this.loadData().then(() => {
                if (this.offer?.availabilityOffers.length) {
                    this.progressbarCurrentStep = 0
                }
            })
        },

        mounted() {
            this.isOfferForComapny = this.$route.params.type === OfferTypeEnum.COMPANY
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .configure-offer-page {
        &__buttons {
            display: flex;
            justify-content: space-between;
            @include breakpoint(smartphone-large) {
                display: block;
                .button {
                    width: 100%;
                }
            }
            @include breakpoint(tablet) {
                .button {
                    width: 100%;
                }
            }
        }
    }
</style>
